import { CheckCircle } from "@/atoms-react/check-circle/check-circle.react";
import Column from "@/atoms-react/column/Column.react";
import Image from "@/atoms-react/image/Image.react";
import { Base, BigTitle } from "@/atoms-react/text/Text.react";
import { Key, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { LoginSpaceshipAnimation } from "@/components/animations/login-spaceship/index.react";
import {
  AttributesHtmlElement,
  partnerElementDataType,
  typeElement
} from "@/features/partner/types";
import { convertToDesignArray, convertToDesignCards } from "@/features/partner/utils";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { twMerge } from "tailwind-merge";

export const OlkyIntroduction = ({
  mode: _mode,
  data
}: {
  mode: "short" | "long";
  data: partnerElementDataType | undefined;
}) => {
  const { t } = useTranslation(["pass"]);
  const timeouts = useRef<NodeJS.Timeout | null>(null);
  const [mode, setMode] = useState<"short" | "long">(_mode);
  const [visible, setVisible] = useState(true);
  const origin = useOrigin();
  const { getLanguage } = usei18n();
  // Fade in fade out
  useEffect(() => {
    if (mode === _mode) return;
    setVisible(false);
    if (timeouts.current) clearTimeout(timeouts.current);
    timeouts.current = setTimeout(() => {
      setMode(_mode);
      if (timeouts.current) clearTimeout(timeouts.current);
      timeouts.current = setTimeout(() => setVisible(true), 200);
    }, 200);
  }, [_mode]);
  const getSteps = () => {
    if (origin === FromURL.None)
      return [
        t("flow.id_control.olky_intro.steps.1"),
        t("flow.id_control.olky_intro.steps.2"),
        t("flow.id_control.olky_intro.steps.3"),
        t("flow.id_control.olky_intro.steps.4")
      ];
    const steps = convertToDesignArray(data?.cards?.[0], typeElement.STEP);
    return steps?.map((step) => (getLanguage() === "fr" ? step?.content_fr : step?.content_en));
  };
  const steps = getSteps() ?? [];

  const getHtmlSteps = (() => {
    if (origin === FromURL.None) return [];
    const htmlSteps = convertToDesignArray(data?.cards?.[0], typeElement.HTML_STEP);
    const htmlStepsArry = htmlSteps?.map((htmlStep) =>
      getLanguage() === "fr" ? (htmlStep.content_fr ?? "") : (htmlStep.content_en ?? "")
    );
    return htmlStepsArry ?? [];
  })();
  const getAttribute = (() => {
    return data?.cards?.[0]?.attribues;
  })();
  const htmlSteps = getHtmlSteps ?? [];
  const getBigTitle = (() => {
    if (origin === FromURL.None) return t("flow.id_control.olky_intro.title.default");
    const title = convertToDesignCards(data?.cards?.[0], typeElement.TITLE);
    return getLanguage() === "fr" ? title?.content_fr : title?.content_en;
  })();
  const getBigHtmlTitle = (() => {
    const htmlTitle = convertToDesignCards(data?.cards?.[0], typeElement.HTML_TITLE);
    return getLanguage() === "fr" ? htmlTitle?.content_fr : htmlTitle?.content_en;
  })();

  const getDescription = (() => {
    if (origin === FromURL.None) return t("flow.id_control.olky_intro.desc.default");
    const description = convertToDesignCards(data?.cards?.[0], typeElement.DESCRIPTION);
    return getLanguage() === "fr" ? description?.content_fr : description?.content_en;
  })();
  const getLogo = (() => {
    if (origin === FromURL.None) return "/images/olkypass/logo-olky-pass.svg";
    const logo = convertToDesignCards(data?.cards?.[0], typeElement.LOGO);
    return getLanguage() === "fr" ? logo?.url_fr : logo?.url_en;
  })();
  const getFooter = (() => {
    const text = convertToDesignCards(data?.cards?.[0], typeElement.FOOTER);
    return getLanguage() === "fr" ? text?.content_fr : text?.content_en;
  })();
  return (
    <div
      className={
        "relative flex flex-col items-center transition-all" +
        (visible ? "opacity-100" : "opacity-0")
      }
    >
      {mode !== "short" && (
        <>
          <div className="z-10 w-full max-w-2xl xl:px-8">
            {getLogo ? (
              <Image
                src={getLogo}
                className={"mb-6 inline-block hidden w-48 lg:block"}
                alt="Olky Logo"
              />
            ) : (
              <div></div>
            )}
            {getBigHtmlTitle ? (
              <BigTitle
                className={twMerge(
                  "pb-4 text-white",
                  getAttribute === AttributesHtmlElement.white && "text-black"
                )}
                dangerouslySetInnerHTML={{ __html: getBigHtmlTitle }}
              ></BigTitle>
            ) : (
              <BigTitle
                className={twMerge(
                  "pb-4 text-white",
                  getAttribute === AttributesHtmlElement.white && "text-black"
                )}
              >
                {getBigTitle}
              </BigTitle>
            )}
            <Base className={"hidden pb-8 text-white opacity-95 lg:block"}>{getDescription}</Base>

            <Column className="hidden lg:block">
              {steps.map((step, index) => (
                <div key={index} className={"flex pb-6"}>
                  <CheckCircle className={"bg-primary"} />
                  <Base
                    className={twMerge(
                      "w-full break-normal font-semibold text-white",
                      origin !== FromURL.None &&
                        getAttribute === AttributesHtmlElement.white &&
                        "text-black"
                    )}
                  >
                    {step}
                  </Base>
                </div>
              ))}
              {htmlSteps.map((htmlStep: string, index: Key | null | undefined) => (
                <div key={index} className={"flex pb-6"}>
                  <CheckCircle className={"bg-primary"} />
                  <Base
                    className={twMerge(
                      "w-full break-normal font-semibold text-white",
                      origin !== FromURL.None &&
                        getAttribute === AttributesHtmlElement.white &&
                        "text-black"
                    )}
                    dangerouslySetInnerHTML={{ __html: htmlStep }}
                  ></Base>
                </div>
              ))}

              {getFooter && <div dangerouslySetInnerHTML={{ __html: getFooter }}></div>}
            </Column>
            <div className={"mb-6 hidden h-8 lg:block"} />
          </div>
          <div
            className={"z-[-1]" + "top-0 right-0 bottom-0 left-0 hidden opacity-50 lg:absolute"}
          >
            <Image
              alt="Olky Planet Blue"
              src="/images/olkypass/olky-planet-blue.png"
              className={`absolute -top-32 -left-8 z-[-1]` + "lg:h-60 lg:w-60"}
            />
            <Image
              alt="Olky Planet Yellow"
              src="/images/olkypass/olky-planet-yellow.png"
              className={`absolute -top-32 -right-16 z-[-1]` + "h-60 w-60"}
            />
            <Image
              alt="Olky Planet Orange"
              src="/images/olkypass/olky-planet-orange.png"
              className={`absolute right-0 -bottom-32 z-[-1]` + "h-60 w-60"}
            />
          </div>
        </>
      )}
      {mode === "short" && origin !== FromURL.None && (
        <>
          <div className="z-10 w-full max-w-2xl xl:px-8">
            {getLogo && (
              <Image
                src={getLogo}
                className={"mb-6 inline-block hidden w-48 lg:block"}
                alt="Olky Logo"
              />
            )}
            {getBigHtmlTitle ? (
              <BigTitle
                className={twMerge(
                  "pb-4 text-white",
                  getAttribute === AttributesHtmlElement.white && "text-black"
                )}
                dangerouslySetInnerHTML={{ __html: getBigHtmlTitle }}
              ></BigTitle>
            ) : (
              <BigTitle
                className={twMerge(
                  "pb-4 text-white",
                  getAttribute === AttributesHtmlElement.white && "text-black"
                )}
              >
                {getBigTitle}
              </BigTitle>
            )}
            <Base className={"hidden pb-8 text-white opacity-95 lg:block"}>{getDescription}</Base>
            <Column className="hidden lg:block">
              {steps.map((step, index) => (
                <div key={index} className={"flex pb-6"}>
                  <CheckCircle className={"bg-primary"} />
                  <Base
                    className={twMerge(
                      "w-full break-normal font-semibold text-white",
                      getAttribute === AttributesHtmlElement.white && "text-black"
                    )}
                  >
                    {step}
                  </Base>
                </div>
              ))}
              {htmlSteps.map((htmlStep: string, index: Key | null | undefined) => (
                <div key={index} className={"flex pb-6"}>
                  <CheckCircle className={"bg-primary"} />
                  <Base
                    className={twMerge(
                      "w-full break-normal font-semibold text-white",
                      getAttribute === AttributesHtmlElement.white && "text-black"
                    )}
                    dangerouslySetInnerHTML={{ __html: htmlStep }}
                  ></Base>
                </div>
              ))}
              {getFooter && <div dangerouslySetInnerHTML={{ __html: getFooter }}></div>}{" "}
            </Column>
            <div className={"mb-6 hidden h-8 lg:block"} />
          </div>
          <div
            className={"z-[-1]" + "top-0 right-0 bottom-0 left-0 hidden opacity-50 lg:absolute"}
          >
            <Image
              alt="Olky Planet Blue"
              src="/images/olkypass/olky-planet-blue.png"
              className={`absolute -top-32 -left-8 z-[-1]` + "lg:h-60 lg:w-60"}
            />
            <Image
              alt="Olky Planet Yellow"
              src="/images/olkypass/olky-planet-yellow.png"
              className={`absolute -top-32 -right-16 z-[-1]` + "h-60 w-60"}
            />
            <Image
              alt="Olky Planet Orange"
              src="/images/olkypass/olky-planet-orange.png"
              className={`absolute right-0 -bottom-32 z-[-1]` + "h-60 w-60"}
            />
          </div>
        </>
      )}

      {mode === "short" && origin === FromURL.None && (
        <LoginSpaceshipAnimation className="z-10 flex h-dvh flex-col items-center justify-center" />
      )}
    </div>
  );
};
